import React, { useState, useEffect } from 'react';
import { useSearchParams, useLocation } from 'react-router-dom';
import './RangeSlider.css';

const RangeSlider = ({
  title,
  minBound,
  maxBound,
  minParam,
  maxParam,
  prefix = '',
  setPendingFilters,
  pendingFilters,
  minKey,
  maxKey,
}) => {
  const [minMaxRange, setMinMaxRange] = useState({
    minVal: minBound,
    maxVal: maxBound,
  });

  const [minInput, setMinInput] = useState('');
  const [maxInput, setMaxInput] = useState('');

  const [searchParams] = useSearchParams();
  const location = useLocation();

  // Sync with pendingFilters on mount
  useEffect(() => {
    if (pendingFilters) {
      const newMin = pendingFilters[minKey] || minBound;
      const newMax = pendingFilters[maxKey] || maxBound;
      setMinMaxRange({ minVal: newMin, maxVal: newMax });
      setMinInput(newMin === minBound ? '' : String(newMin));
      setMaxInput(newMax === maxBound ? '' : String(newMax));
    }
  }, [pendingFilters, minKey, maxKey, minBound, maxBound]);

  // Update values & sync with pendingFilters
  const updateValues = (newMin, newMax) => {
    const clampedMin = Math.max(minBound, Math.min(newMin, newMax - 1));
    const clampedMax = Math.min(maxBound, Math.max(newMax, clampedMin + 1));

    setMinMaxRange({ minVal: clampedMin, maxVal: clampedMax });
    setMinInput(clampedMin === minBound ? '' : String(clampedMin));
    setMaxInput(clampedMax === maxBound ? '' : String(clampedMax));

    setPendingFilters(prev => ({
      ...prev,
      [minKey]: clampedMin,
      [maxKey]: clampedMax,
    }));
  };

  const handleRangeChange = e => {
    const { name, value } = e.target;
    const val = +value;
    if (name === minKey) updateValues(val, minMaxRange.maxVal);
    if (name === maxKey) updateValues(minMaxRange.minVal, val);
  };

  const handleMinInputChange = e => setMinInput(e.target.value);
  const handleMaxInputChange = e => setMaxInput(e.target.value);

  // Normalize input on blur
  const handleMinBlur = () => {
    let n = +minInput;
    if (isNaN(n) || n < minBound) n = minBound;
    if (n >= minMaxRange.maxVal) n = minMaxRange.maxVal - 1;
    updateValues(n, minMaxRange.maxVal);
  };

  const handleMaxBlur = () => {
    let n = +maxInput;
    if (isNaN(n) || n > maxBound) n = maxBound;
    if (n <= minMaxRange.minVal) n = minMaxRange.minVal + 1;
    updateValues(minMaxRange.minVal, n);
  };

  // Sync with URL query params
  useEffect(() => {
    const minStr = searchParams.get(minParam),
      maxStr = searchParams.get(maxParam);

    if (searchParams.get('apply')) {
      const minQ = minStr ? +minStr : NaN,
        maxQ = maxStr ? +maxStr : NaN;

      const newMin = Number.isFinite(minQ) ? Math.max(minBound, Math.min(minQ, maxBound)) : minBound;
      const newMax = Number.isFinite(maxQ) ? Math.max(minBound, Math.min(maxQ, maxBound)) : maxBound;

      updateValues(newMin, newMax);
    }
  }, [location.search, minParam, maxParam, minBound, maxBound]);

  const leftPct = ((minMaxRange.minVal - minBound) / (maxBound - minBound)) * 100;
  const rightPct = ((minMaxRange.maxVal - minBound) / (maxBound - minBound)) * 100;

  return (
    <div className="product_filteration_section">
      <div className="price_filteration">
        <h1 className="filter_heading">{title}</h1>

        <div className="slider" style={{ position: 'relative' }}>
          <div
            className="price_selected_range"
            style={{
              left: `${leftPct}%`,
              width: `${Math.max(0, rightPct - leftPct)}%`,
            }}
          />
          <div className="slider_label" style={{ left: `${leftPct}%` }}>
            {prefix}
            {minMaxRange.minVal}
          </div>
          <div className="slider_label" style={{ left: `${rightPct}%` }}>
            {prefix}
            {minMaxRange.maxVal}
          </div>

          <input
            name={minKey}
            type="range"
            min={minBound}
            max={maxBound}
            step={1}
            value={minMaxRange.minVal}
            onChange={handleRangeChange}
          />
          <input
            name={maxKey}
            type="range"
            min={minBound}
            max={maxBound}
            step={1}
            value={minMaxRange.maxVal}
            onChange={handleRangeChange}
          />
        </div>

        <div className="slider_values">
          <span>{prefix + minMaxRange.minVal}</span>
          <span>{prefix + minMaxRange.maxVal}</span>
        </div>

        <div className="input_boxes_container">
          <div className="input_box_group">
            <label className="input_label">Min</label>
            <div className="input_wrapper">
              {prefix && <span className="input_prefix">{prefix}</span>}
              <input
                name={minKey}
                type="number"
                value={minInput}
                onChange={handleMinInputChange}
                onBlur={handleMinBlur}
                placeholder={String(minBound)}
                min={minBound}
                max={maxBound - 1}
                className="range_input"
              />
            </div>
          </div>

          <div className="input_box_group">
            <label className="input_label">Max</label>
            <div className="input_wrapper">
              {prefix && <span className="input_prefix">{prefix}</span>}
              <input
                name={maxKey}
                type="number"
                value={maxInput}
                onChange={handleMaxInputChange}
                onBlur={handleMaxBlur}
                placeholder={String(maxBound)}
                min={minBound + 1}
                max={maxBound}
                className="range_input"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RangeSlider;
